import { Signing } from "pandadoc-signing";
import { EventEmitter } from "./emitter";
import type {
  ContractConfig,
  ContractErrorCode,
  ContractErrorPayload,
  ContractEventHandler,
  ContractEventName,
  ContractEventPayload,
  ContractEventsMap,
} from "./types";

// ─── PandaDoc → Quantum event mapping ────────────────────────────────────────
// Canonical mapping verified against pandadoc-signing/dist/signing.js.
// PandaDoc emits 9 events; we surface 5 Quantum events.

type PDNonErrorEvent =
  | "document.loading"
  | "document.loaded"
  | "document.rendered"
  | "document.completed";

type PDErrorEvent =
  | "document.not.found"
  | "document.protected"
  | "document.attempts.limit.exceeded"
  | "document.loading.error"
  | "document.exception";

type PDEventName = PDNonErrorEvent | PDErrorEvent;

const PD_NON_ERROR_MAP: Readonly<Record<PDNonErrorEvent, ContractEventName>> =
  Object.freeze({
    "document.loading": "contract.loading",
    "document.loaded": "contract.loaded",
    "document.rendered": "contract.ready",
    "document.completed": "contract.completed",
  });

const PD_ERROR_CODE_MAP: Readonly<Record<PDErrorEvent, ContractErrorCode>> =
  Object.freeze({
    "document.not.found": "not_found",
    "document.protected": "verification_required",
    "document.attempts.limit.exceeded": "verification_limit_exceeded",
    "document.loading.error": "loading_error",
    "document.exception": "unknown",
  });

// Derive the full set from the maps so adding/removing a mapping can't drift
// the listener registration. Frozen to make accidental runtime mutation an error.
const ALL_PD_EVENTS: ReadonlyArray<PDEventName> = Object.freeze([
  ...(Object.keys(PD_NON_ERROR_MAP) as PDNonErrorEvent[]),
  ...(Object.keys(PD_ERROR_CODE_MAP) as PDErrorEvent[]),
]);

/**
 * Maximum length of a sanitized error message exposed to partners.
 * Prevents abnormally large or attacker-influenced vendor strings from being
 * forwarded into partner telemetry/RUM.
 */
const MAX_ERROR_MESSAGE_LENGTH = 200;

/**
 * Strip vendor identifiers and unsafe content from an error message before
 * exposing it to partners via `contract.error.message`. The wrapper is a
 * vendor-neutral abstraction; partners must not see PandaDoc references,
 * vendor UUIDs, or control characters in their error stream.
 *
 * Scope: trusted vendor output only. This is NOT a general-purpose user-input
 * sanitizer — Unicode-confusable attacks (e.g. Cyrillic look-alikes) and
 * homoglyph manipulation are out of the threat model. The vendor (PandaDoc)
 * emits ASCII strings.
 */
function sanitizeErrorMessage(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  let s = raw;
  // Strip Unicode format chars (Cf — zero-width joiner, byte-order mark,
  // bidi controls) with EMPTY string so they can't be used to split brand
  // names: "Panda‍Doc" (with U+200D ZWJ between) → "PandaDoc" before the
  // brand pass runs. These chars are zero-width by definition; deleting them
  // doesn't merge unrelated visible tokens.
  s = s.replace(/\p{Cf}/gu, "");
  // Strip C0/C1 control chars (Cc) with a space so they can't break log/UI
  // rendering. Unlike Cf chars, control chars are not zero-width — replacing
  // with space preserves token separation.
  s = s.replace(/\p{Cc}/gu, " ");
  // Strip vendor hostnames before the bare brand pass so we don't leave ".com"
  // / ".eu" survivors (a strong vendor hint even without the brand name).
  s = s.replace(/\bpandadoc\.(?:com|eu)\b/gi, "");
  // Strip the vendor name itself.
  s = s.replace(/pandadoc/gi, "");
  // Vendor's class/library name with optional "SDK" suffix separated by
  // whitespace, hyphen, or underscore (covers "Signing SDK", "Signing-SDK").
  s = s.replace(/\bsigning(?:[\s\-_]+sdk)?\b/gi, "vendor");
  // Strip vendor UUIDs / opaque ids (8-4-4-4-12 hex).
  s = s.replace(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
    "<id>",
  );
  // Strip PD-prefixed identifiers (PandaDoc internal ids).
  s = s.replace(/\bPD-?[0-9a-f]{6,}\b/gi, "<id>");
  // Collapse whitespace introduced by replacements.
  s = s.replace(/\s+/g, " ").trim();
  if (!s) return undefined;
  if (s.length > MAX_ERROR_MESSAGE_LENGTH) {
    s = `${s.slice(0, MAX_ERROR_MESSAGE_LENGTH - 3)}...`;
  }
  return s;
}

/**
 * Generate a random container id for HTMLElements that arrive without one.
 * Uses `crypto.randomUUID()` when available (modern browsers + Node ≥19);
 * falls back to a timestamp + Math.random for older environments.
 */
function generateContainerId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    try {
      return `qc-container-${crypto.randomUUID().slice(0, 8)}`;
    } catch {
      // crypto.randomUUID throws in insecure (non-localhost http) contexts.
      // Fall through to the timestamp-based fallback.
    }
  }
  return `qc-container-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Apply a CSS dimension value to an element's inline style. Numeric values
 * are interpreted as pixels; strings are applied verbatim (e.g. "100%", "50vw").
 */
function applyDimension(
  el: HTMLElement,
  axis: "width" | "height",
  value: number | string | undefined,
): void {
  if (value === undefined) return;
  el.style[axis] = typeof value === "number" ? `${value}px` : value;
}

export class QuantumContracts {
  private emitter = new EventEmitter();
  private signing: Signing | null = null;
  private config: ContractConfig;
  private resolvedContainerId: string;
  private containerEl: HTMLElement | null = null;

  /**
   * Set on first successful signing.open(); cleared on close().
   * Mutations are gated by epoch checks so concurrent close()/destroy() during
   * an in-flight open() can't leave this stuck-true on a torn-down instance.
   */
  private isOpenState = false;
  /** Synchronous "open() is in progress" guard — set before any await. */
  private isOpening = false;
  /** Set by destroy(); terminal — open() throws after this is set. */
  private isDestroyed = false;
  /**
   * Monotonic counter incremented on every open()/close()/destroy(). Used by
   * the open() success/failure continuations to detect "the lifecycle moved
   * on while I was awaiting" and bail before mutating state.
   */
  private lifecycleEpoch = 0;
  /**
   * Bridge handlers registered on the vendor Signing instance. Tracked so
   * destroy() can call signing.off() and release the strong refs that would
   * otherwise leak this instance via the vendor's internal listener map.
   */
  private bridgeHandlers: Array<{
    event: string;
    handler: (rawPayload: unknown) => void;
  }> = [];

  constructor(container: string | HTMLElement, config: ContractConfig) {
    this.config = config;

    if (typeof container === "string") {
      this.resolvedContainerId = container;
    } else {
      if (!container.id) {
        container.id = generateContainerId();
      }
      this.resolvedContainerId = container.id;
      this.containerEl = container;
    }
  }

  /**
   * Open the signing experience.
   *
   * Lifecycle contract:
   * - First call: constructs the underlying signing instance and renders the iframe.
   * - After `close()`: the prior vendor instance is destroyed; this call
   *   constructs a fresh one. Partners do NOT need to re-subscribe to events —
   *   subscriptions live on the SDK's own emitter, not the vendor.
   * - After `destroy()`: throws — the instance is terminal.
   * - Concurrent open() (calling open() while another open() is still
   *   awaiting): throws synchronously. The SDK does not silently dispatch
   *   two vendor opens.
   * - If close() or destroy() runs while open() is awaiting, the awaiting
   *   continuation detects the lifecycle change and bails without mutating
   *   state.
   *
   * On vendor-side construction or open failures, emits `contract.error`
   * (with `code: 'loading_error'`) instead of rejecting, so partners have
   * one consistent error channel via the event API.
   */
  async open(): Promise<void> {
    if (this.isDestroyed) {
      throw new Error(
        "QuantumContracts: instance has been destroyed. Construct a new instance to open another signing session.",
      );
    }
    if (this.isOpening) {
      throw new Error(
        "QuantumContracts: open() already in progress. Wait for the prior call to settle.",
      );
    }
    if (this.isOpenState) {
      throw new Error(
        "QuantumContracts: already open. Call close() or destroy() first.",
      );
    }

    // Reserve the operation synchronously, before any await, so concurrent
    // open() calls in the same tick reliably fail the guard above.
    this.isOpening = true;
    const epoch = ++this.lifecycleEpoch;

    try {
      // Validate the signingToken — vendor's silent early-return on a falsy
      // sessionId leaves the wrapper "open" with no actual signing happening.
      // Surface the misconfiguration via the same error channel partners use
      // for runtime failures.
      if (
        typeof this.config.signingToken !== "string" ||
        this.config.signingToken.length === 0
      ) {
        queueMicrotask(() => {
          if (!this.isDestroyed && epoch === this.lifecycleEpoch) {
            this.emitter.emit("contract.error", {
              contractId: this.config.contractId,
              code: "loading_error",
              message:
                "signingToken is required and must be a non-empty string",
            });
          }
        });
        return;
      }

      // Resolve the container element if we haven't yet.
      if (!this.containerEl) {
        const el = document.getElementById(this.resolvedContainerId);
        if (!el) {
          throw new Error(
            `QuantumContracts: container element "#${this.resolvedContainerId}" not found in the DOM.`,
          );
        }
        this.containerEl = el;
      }

      this.log(
        "Opening contract session:",
        this.redactToken(this.config.signingToken),
      );

      // Construct the vendor Signing instance. Each open() builds a fresh
      // instance because close() always tears down (see close()'s contract).
      if (!this.signing) {
        let signing: Signing;
        try {
          signing = new Signing(
            this.resolvedContainerId,
            {
              sessionId: this.config.signingToken,
              // Intentionally omitted:
              // - debugMode: vendor logs the full constructor config (incl.
              //   sessionId) when debugMode=true. Our own redacted logging
              //   uses redactToken().
              // - width/height: vendor unconditionally appends "px", breaking
              //   string values like "100%". We apply dimensions to the
              //   iframe ourselves AFTER construction.
            },
            { region: "com" },
          );
        } catch (err) {
          this.logError("Constructor error:", err);
          const message = sanitizeErrorMessage(
            err instanceof Error ? err.message : String(err),
          );
          // Defer to a microtask so a partner who registers a handler in the
          // same synchronous block (e.g. `qc.open(); qc.on('contract.error', …)`)
          // still receives the event. Suppress if destroyed or if a later
          // open()/close()/destroy() bumped the epoch before the microtask
          // fires.
          queueMicrotask(() => {
            if (!this.isDestroyed && epoch === this.lifecycleEpoch) {
              this.emitter.emit("contract.error", {
                contractId: this.config.contractId,
                code: "loading_error",
                ...(message !== undefined ? { message } : {}),
              });
            }
          });
          return;
        }

        this.signing = signing;
        this.registerBridgeListeners(signing);
        // Vendor's constructor replaces our container element with an iframe
        // that keeps the same id. Apply dimensions now (post-construction).
        // If the iframe is missing or not an <iframe>, surface as a loading
        // error instead of silently dropping the dimensions.
        const dimensionResult = this.applyDimensionsToIframe();
        if (dimensionResult === "iframe-missing") {
          const position = this.captureContainerPosition();
          this.tearDownVendorInstance();
          this.restoreFreshContainer(position);
          // Deferred to a microtask for consistency with the constructor-throw
          // path above — same-tick subscribers should still receive the event.
          queueMicrotask(() => {
            if (!this.isDestroyed && epoch === this.lifecycleEpoch) {
              this.emitter.emit("contract.error", {
                contractId: this.config.contractId,
                code: "loading_error",
                message:
                  "Container element was not replaced with an iframe as expected",
              });
            }
          });
          return;
        }
      }

      // Awaiting boundary — anything beyond here may execute after close()
      // or destroy() bumped the epoch. Re-check before any state mutation.
      try {
        await this.signing.open();
      } catch (err) {
        this.logError("Signing.open() rejected:", err);
        // Lifecycle moved on (close/destroy ran) — bail silently.
        if (this.isDestroyed || epoch !== this.lifecycleEpoch) return;
        // Tear down the vendor instance: its internal state is left as
        // "idle" after a failed open, and a retry would silently no-op
        // because vendor's open() only proceeds from "ready" or "closed".
        // Null this.signing so the next open() reconstructs a fresh
        // instance, and restore a fresh container at the iframe's original
        // DOM position so the partner's retry call doesn't have to re-attach.
        const position = this.captureContainerPosition();
        this.tearDownVendorInstance();
        this.restoreFreshContainer(position);
        const message = sanitizeErrorMessage(
          err instanceof Error ? err.message : String(err),
        );
        // Defer the emit to a microtask so this.finally() can release the
        // isOpening guard FIRST. Otherwise a partner who auto-retries from
        // inside the contract.error handler hits "open() already in progress"
        // because we're still synchronously inside the open() call when the
        // handler runs. Same shape as the constructor-throw path above.
        queueMicrotask(() => {
          if (!this.isDestroyed && epoch === this.lifecycleEpoch) {
            this.emitter.emit("contract.error", {
              contractId: this.config.contractId,
              code: "loading_error",
              ...(message !== undefined ? { message } : {}),
            });
          }
        });
        return;
      }

      // Same-epoch + not destroyed: success continuation.
      if (this.isDestroyed || epoch !== this.lifecycleEpoch) return;
      this.isOpenState = true;
    } finally {
      // Only release the in-progress guard if WE are still the current
      // operation. If close()/destroy()/a newer open() bumped the epoch
      // (and likely already reset isOpening themselves), don't clobber the
      // newer call's state by setting it back to false here.
      if (epoch === this.lifecycleEpoch) {
        this.isOpening = false;
      }
    }
  }

  /**
   * Close the signing UI and tear down the underlying vendor instance.
   *
   * Why always tear down: the vendor SDK auto-opens (and posts the token)
   * when its iframe finishes loading, regardless of whether we asked it to.
   * Calling the vendor's own `close()` is a no-op before that ready event
   * fires — so a "close before ready" leaves the token in flight. By
   * destroying the vendor instance + iframe, we cancel the deferred auto-open
   * unconditionally.
   *
   * Effect on next open():
   * - The vendor instance is reconstructed (a fresh iframe loads).
   * - Partner event subscriptions are preserved (they live on the SDK's own
   *   emitter, not the vendor).
   * - The container element is restored at its original DOM position
   *   (preserving sibling order) so the partner does not need to re-attach
   *   anything.
   *
   * Synchronous from the partner's perspective. Idempotent: safe to call
   * multiple times or before `open()`. Also resets `isOpening` so a rapid
   * synchronous `open(); close(); open();` sequence (e.g. partner double-
   * clicks "cancel and try again") doesn't deadlock on the in-progress guard.
   */
  close(): void {
    if (this.isDestroyed) return;
    this.lifecycleEpoch++;
    this.isOpenState = false;
    // Reset isOpening so a synchronous re-open in the same tick can proceed.
    // Any stale in-flight open() awaiting on the vendor will detect the
    // epoch bump and bail without mutating state.
    this.isOpening = false;
    if (!this.signing) return;

    const position = this.captureContainerPosition();
    this.tearDownVendorInstance();
    this.restoreFreshContainer(position);
  }

  /**
   * Destroy the instance — terminal. Unwires our bridge listeners from the
   * vendor instance, calls vendor destroy() (which removes the iframe and
   * may detach the container element), clears all event subscriptions, and
   * marks the instance unusable. Subsequent `open()` calls will throw.
   *
   * Idempotent: safe to call without a prior `open()` or more than once.
   */
  destroy(): void {
    if (this.isDestroyed) return;
    this.lifecycleEpoch++;
    // Same reason as close(): release the synchronous in-progress guard so
    // it doesn't get stuck-true if destroy() runs while open() is awaiting.
    this.isOpening = false;
    // tearDownVendorInstance() swallows vendor throws internally; no outer
    // catch needed.
    this.tearDownVendorInstance();
    this.isOpenState = false;
    this.isDestroyed = true;
    this.emitter.removeAllListeners();
    this.log("Instance destroyed.");
  }

  /** Register an event handler. Returns `this` for chaining. */
  on<K extends ContractEventName>(
    event: K,
    handler: ContractEventHandler<K>,
  ): this {
    this.emitter.on(event, handler);
    return this;
  }

  /** Register a one-time event handler. Returns `this` for chaining. */
  once<K extends ContractEventName>(
    event: K,
    handler: ContractEventHandler<K>,
  ): this {
    this.emitter.once(event, handler);
    return this;
  }

  /** Remove an event handler (or all handlers for the event if omitted). */
  off<K extends ContractEventName>(
    event: K,
    handler?: ContractEventHandler<K>,
  ): this {
    this.emitter.off(event, handler);
    return this;
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  /**
   * Register bridge listeners on the vendor Signing instance. Each handler
   * is recorded in `bridgeHandlers` so destroy()/teardown can release the
   * vendor's strong refs.
   */
  private registerBridgeListeners(signing: Signing): void {
    for (const pdEvent of ALL_PD_EVENTS) {
      const handler = (rawPayload: unknown): void => {
        // Drop events arriving after destroy() under any circumstance.
        if (this.isDestroyed) return;
        // Intentionally log only the event name, NOT the raw payload. Vendor
        // payloads can carry recipient PII (email, name, company) and we
        // don't want partners with debug:true accidentally piping that into
        // console/RUM/screenshots.
        this.log(`Event: ${pdEvent}`);
        this.bridgeEvent(pdEvent, rawPayload);
      };
      // PandaDoc's SigningEventsMap only declares 3 events in .d.ts but
      // emits 9 at runtime. Cast to satisfy the type checker.
      (signing.on as (event: string, h: (payload: unknown) => void) => unknown)(
        pdEvent,
        handler,
      );
      this.bridgeHandlers.push({ event: pdEvent, handler });
    }
  }

  /**
   * Apply partner-supplied dimensions to the iframe the vendor created.
   * Vendor's constructor replaced our container with an iframe that keeps
   * the same id, so we query by id to find it. Returns "iframe-missing" if
   * the post-replace element is missing or unexpected — the caller surfaces
   * that as a loading error.
   */
  private applyDimensionsToIframe(): "ok" | "iframe-missing" {
    const el = document.getElementById(this.resolvedContainerId);
    if (!el || el.tagName !== "IFRAME") {
      this.log(
        "Container did not become an iframe post-construction",
        el ? `(saw <${el.tagName.toLowerCase()}>)` : "(missing)",
      );
      return "iframe-missing";
    }
    if (this.config.width === undefined && this.config.height === undefined) {
      return "ok";
    }
    applyDimension(el, "width", this.config.width);
    applyDimension(el, "height", this.config.height);
    return "ok";
  }

  /**
   * Unwire bridge listeners from the vendor instance, call vendor destroy(),
   * and null our reference. Used both by destroy() (terminal) and by the
   * open()-rejection retry path (so a fresh signing instance can be built).
   *
   * Both the per-listener off() calls and the vendor destroy() are wrapped
   * in try/catch — a vendor exception in either must not abort the rest of
   * the teardown or the calling control flow (e.g. open()'s rejection path
   * which still needs to emit contract.error after teardown).
   */
  private tearDownVendorInstance(): void {
    if (!this.signing) return;
    const current = this.signing;
    try {
      const signingWithOff = current as unknown as {
        off?: (event: string, h: (payload: unknown) => void) => unknown;
      };
      if (typeof signingWithOff.off === "function") {
        for (const { event, handler } of this.bridgeHandlers) {
          try {
            signingWithOff.off(event, handler);
          } catch (err) {
            this.logError(`Vendor off(${event}) threw:`, err);
          }
        }
      }
      this.bridgeHandlers = [];
      try {
        current.destroy();
      } catch (err) {
        this.logError("Vendor destroy() threw:", err);
      }
    } finally {
      this.signing = null;
    }
  }

  /**
   * Snapshot the DOM position of the element currently at our container id.
   * Captured BEFORE tearDownVendorInstance() removes the iframe so we can
   * restore a fresh container at the same parent + sibling slot afterward.
   */
  private captureContainerPosition(): {
    parent: ParentNode | null;
    nextSibling: ChildNode | null;
  } {
    const el = document.getElementById(this.resolvedContainerId);
    return {
      parent: el?.parentNode ?? null,
      nextSibling: el?.nextSibling ?? null,
    };
  }

  /**
   * Insert a fresh `<div id={resolvedContainerId}>` at the captured DOM
   * position so a subsequent open() can find it. Uses `insertBefore` to
   * preserve sibling order; falls back to `appendChild` when the iframe was
   * the last child. Nulls `this.containerEl` so the next open() re-resolves
   * via `getElementById` (in case the partner moved the parent in between).
   */
  private restoreFreshContainer(position: {
    parent: ParentNode | null;
    nextSibling: ChildNode | null;
  }): void {
    if (position.parent) {
      const fresh = document.createElement("div");
      fresh.id = this.resolvedContainerId;
      if (position.nextSibling) {
        position.parent.insertBefore(fresh, position.nextSibling);
      } else {
        position.parent.appendChild(fresh);
      }
    }
    this.containerEl = null;
  }

  private bridgeEvent(pdEvent: PDEventName, rawPayload: unknown): void {
    const contractId = this.config.contractId;

    if (Object.hasOwn(PD_NON_ERROR_MAP, pdEvent)) {
      const qEvent = PD_NON_ERROR_MAP[pdEvent as PDNonErrorEvent];
      const payload: ContractEventPayload = { contractId };
      this.emitter.emit(qEvent, payload as ContractEventsMap[typeof qEvent]);
    } else {
      const code = PD_ERROR_CODE_MAP[pdEvent as PDErrorEvent];
      const message = this.extractMessage(rawPayload);
      const payload: ContractErrorPayload = {
        contractId,
        code,
        ...(message !== undefined ? { message } : {}),
      };
      this.emitter.emit("contract.error", payload);
    }
  }

  /**
   * Extract a partner-safe message from a raw vendor payload. Strips vendor
   * identifiers + control chars + caps length via sanitizeErrorMessage.
   */
  private extractMessage(raw: unknown): string | undefined {
    if (!raw || typeof raw !== "object") return undefined;
    const obj = raw as Record<string, unknown>;
    return sanitizeErrorMessage(obj.message);
  }

  /**
   * Redact a signing token for debug logging. Keeps a short prefix + suffix
   * for session correlation but never logs the full credential, even behind
   * the debug flag — partners may enable debug in production and accidentally
   * leak tokens via console output captured by RUM, screenshots, or copy-paste.
   */
  private redactToken(token: string): string {
    if (token.length <= 8) return "***";
    return `${token.slice(0, 4)}…${token.slice(-4)}`;
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) {
      console.log("[QuantumContracts]", ...args);
    }
  }

  /**
   * Debug-log an error from the vendor with the same sanitization we apply
   * to partner-visible `contract.error.message`. Partners may leave
   * `debug: true` enabled in production and pipe console output into RUM /
   * screenshots / support tickets; raw vendor errors can contain UUIDs,
   * PandaDoc identifiers, or other vendor-shaped strings that would otherwise
   * defeat the wrapper's vendor-neutral abstraction.
   */
  private logError(label: string, err: unknown): void {
    if (!this.config.debug) return;
    const raw = err instanceof Error ? err.message : String(err);
    const safe = sanitizeErrorMessage(raw) ?? "<scrubbed>";
    console.log("[QuantumContracts]", label, safe);
  }
}

export default QuantumContracts;
